/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { createSelector, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, shareReplay, Subject, switchMap } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { AsyncJobActions } from '@recruitee/async-job-data';
import { AsyncJobType } from '@recruitee/async-job-types';
import { filterBoolean } from '@recruitee/common';
import {
  RequisitionsActions,
  RequisitionsFilterCountersActions,
  RequisitionsFilterCountersSelectors,
  RequisitionsGlobalActions,
  RequisitionsGroupedActions,
  RequisitionsGroupedSelectors,
  RequisitionsSelectors,
} from '@recruitee/requisitions-data';
import {
  RequisitionsConfigSelectors,
  RequisitionsFlowsSelectors,
} from '@recruitee/requisitions-settings-data';
import type { RequisitionFlow, RequisitionsParams } from '@recruitee/requisitions-types';
import { RequisitionsListParam, RequisitionsScope } from '@recruitee/requisitions-types';

import type { RequisitionsGroupToDisplay } from '../../requisitions.types';

export type RequisitionsData = {
  requisitionsGroups: RequisitionsGroupToDisplay[];
  abilities: RequisitionsAbilities;
  defaultFlowIsMissing: boolean;
  flows: RequisitionFlow[];
  params: RequisitionsParams;
};

export type RequisitionsAbilities = {
  viewRequisitions: boolean;
  viewAllRequisitions: boolean;
  manageRequisitions: boolean;
  manageJobs: boolean;
  manageCompany: boolean;
};

const defaultFlowIsMissing = createSelector(
  RequisitionsConfigSelectors.isDepartmentsMode,
  RequisitionsFlowsSelectors.entities(),
  (isDepartmentsMode, flows) => {
    if (!isDepartmentsMode) return false;
    else return (flows || []).every(flow => !flow.default);
  },
);

@Injectable()
export class RequisitionsListDataService {
  public defaultFlowIsMissing$ = this.store.select(defaultFlowIsMissing);
  public params$: BehaviorSubject<RequisitionsParams> = new BehaviorSubject({
    scope: RequisitionsScope.Active,
  });

  public requisitionsGroups$: Observable<RequisitionsGroupToDisplay[]>;
  public abilities$: Observable<RequisitionsAbilities> = this.store
    .select(AbilitiesSelectors.abilityMap)
    .pipe(
      filterBoolean,
      map(abilities => ({
        viewRequisitions: abilities[Ability.ViewRequisitions],
        viewAllRequisitions: abilities[Ability.ViewAllRequisitions],
        manageRequisitions: abilities[Ability.ManageRequisitions],
        manageJobs: abilities[Ability.ManageJobs],
        manageCompany: abilities[Ability.ManageCompany],
      })),
    );
  public flows$ = this.store.select(RequisitionsFlowsSelectors.entities()).pipe(filterBoolean);
  public filterCounters$ = this.store.select(RequisitionsFilterCountersSelectors.data);
  public isLoaded$: Observable<boolean>;
  public data$: Observable<RequisitionsData>;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private actions$: Actions,
  ) {
    route.queryParams.subscribe(queryParams => {
      this.params$.next({
        ...queryParams,
        [RequisitionsListParam.Scope]:
          queryParams[RequisitionsListParam.Scope] ?? RequisitionsScope.Active,
      });
    });

    this.requisitionsGroups$ = this.params$.pipe(
      switchMap(params => {
        if (params[RequisitionsListParam.GroupBy]) {
          this.store.dispatch(RequisitionsGroupedActions.fetchCollection({ payload: params }));
          return this.store.select(RequisitionsGroupedSelectors.entitiesWithReferences(params));
        }
        this.store.dispatch(RequisitionsActions.fetchCollection({ payload: params }));
        return this.store
          .select(RequisitionsSelectors.entitiesWithReferences(params))
          .pipe(map(requisitions => (requisitions ? [{ requisitions }] : [])));
      }),
      shareReplay(),
    );

    this.isLoaded$ = this.params$.pipe(
      switchMap(params =>
        params[RequisitionsListParam.GroupBy]
          ? this.store.select(RequisitionsGroupedSelectors.isLoaded(params))
          : this.store.select(RequisitionsSelectors.isLoaded(params)),
      ),
    );

    this.data$ = combineLatest([
      this.requisitionsGroups$,
      this.abilities$,
      this.flows$,
      this.params$,
      this.defaultFlowIsMissing$,
    ]).pipe(
      map(([requisitionsGroups, abilities, flows, params, defaultFlowIsMissing]) => ({
        requisitionsGroups,
        abilities,
        flows,
        params,
        defaultFlowIsMissing,
      })),
    );

    this.handleRefreshRequisitions();
    this.handleRefreshOnBulkActionsFinish();
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private handleRefreshRequisitions(): void {
    this.actions$
      .pipe(ofType(RequisitionsGlobalActions.refreshFromWS), withLatestFrom(this.params$))
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([data, params]) => {
        !params[RequisitionsListParam.GroupBy]
          ? this.refreshRequisitions(data, params)
          : this.refreshGroupedRequisitions(params);
      });
  }

  private refreshRequisitions(
    data: { payload: { ids: number[]; all: boolean } },
    params: RequisitionsParams,
  ) {
    data.payload.all
      ? this.store.dispatch(RequisitionsActions.fetchCollection({ payload: params }))
      : data.payload.ids.forEach(id => {
          this.store.dispatch(RequisitionsActions.fetchOne({ payload: { id } }));
        });
    this.store.dispatch(RequisitionsFilterCountersActions.fetch());
  }

  private refreshGroupedRequisitions(params: RequisitionsParams) {
    this.store.dispatch(RequisitionsGroupedActions.fetchCollection({ payload: params }));
    this.store.dispatch(RequisitionsFilterCountersActions.fetch());
  }

  private handleRefreshOnBulkActionsFinish(): void {
    this.actions$
      .pipe(
        ofType(AsyncJobActions.finish),
        filter(({ payload }) =>
          [AsyncJobType.BulkRequisitionsFollow, AsyncJobType.BulkRequisitionsUnfollow].includes(
            payload.job.type,
          ),
        ),
        withLatestFrom(this.params$),
      )
      .subscribe(([asyncJob, params]) => {
        if (params[RequisitionsListParam.GroupBy]) {
          this.store.dispatch(RequisitionsGroupedActions.fetchCollection({ payload: params }));
        } else {
          this.store.dispatch(RequisitionsActions.fetchCollection({ payload: params }));
        }
        this.store.dispatch(RequisitionsFilterCountersActions.fetch());
      });
  }
}
